import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "utils/cn";
import { IconMinus, IconPlus } from "icons";

interface SeatStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  // null / undefined = unlimited (the BE `max_seat` contract uses null for uncapped plans).
  max?: number | null;
  label?: string;
  className?: string;
  disabled?: boolean;
}

// Absolute ceiling so an unlimited (max_seat=null) plan can't overflow via held-down +/typed input.
const HARD_CAP = 999;

// REF-5549 (BE REF-5446): a "Team size" stepper used above the pricing/choose-plan cards.
// It is intentionally a dumb numeric control — per-package seatable/max_seat gating happens in
// the cards, which disable themselves when the chosen seat count exceeds their own limit.
const SeatStepper = ({ value, onChange, min = 1, max = null, label, className, disabled = false }: SeatStepperProps) => {
  const { t } = useTranslation();
  const effectiveMax = max == null ? HARD_CAP : max;

  const commit = (next: number) => {
    if (disabled) return;

    const clamped = Math.min(effectiveMax, Math.max(min, next));
    onChange(clamped);
  };

  const handleInput = (raw: string) => {
    const parsed = parseInt(raw, 10);
    commit(Number.isNaN(parsed) ? min : parsed);
  };

  return (
    <div className={cn("flex items-center gap-3", className)}>
      {label && <span className="text-headline uppercase tracking-wide">{label}</span>}
      <div className="flex items-center gap-1 rounded-full border border-grey-light bg-trueWhite dark:bg-black-300 p-1">
        <button
          type="button"
          aria-label={t("common:stepper.decrease")}
          disabled={disabled || value <= min}
          onClick={() => commit(value - 1)}
          className="flex-center w-8 h-8 rounded-full bg-grey-light/40 hover:bg-grey-light disabled:opacity-30 disabled:pointer-events-none transition"
        >
          <IconMinus className="w-4 h-4 text-trueBlack dark:text-white" />
        </button>
        <input
          type="number"
          inputMode="numeric"
          value={value}
          min={min}
          max={effectiveMax}
          disabled={disabled}
          aria-label={label || t("common:stepper.seats")}
          onChange={(e) => handleInput(e.target.value)}
          className="w-12 bg-transparent text-center text-bodyMd font-bold text-trueBlack dark:text-white outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <button
          type="button"
          aria-label={t("common:stepper.increase")}
          disabled={disabled || value >= effectiveMax}
          onClick={() => commit(value + 1)}
          className="flex-center w-8 h-8 rounded-full bg-grey-light/40 hover:bg-grey-light disabled:opacity-30 disabled:pointer-events-none transition"
        >
          <IconPlus className="w-4 h-4 text-trueBlack dark:text-white" />
        </button>
      </div>
    </div>
  );
};

export default SeatStepper;
